import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GripVertical } from 'lucide-react';

export interface ReservedTableMeta {
  count: number;
  hasSelectedReservation: boolean;
}

const areStringArraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

const getChairPositions = (table: any) => {
  const chairs: { x: number; y: number; rotation: number }[] = [];
  const { shape, maxCovers } = table;
  const max = Math.max(1, Math.floor(Number(maxCovers) || 1));
  const chairSize = 30;
  const half = chairSize / 2;

  if (shape.type === 'square' || shape.type === 'rectangle') {
    const dist = [0, 0, 0, 0];
    let remaining = max;
    for (let i = 0; i < 4; i++) {
      dist[i] = Math.floor(max / 4);
      remaining -= dist[i];
    }
    for (let i = 0; remaining > 0; i++) {
      dist[i % 4] += 1;
      remaining--;
    }

    if (dist[0] > 0) {
      const seg = shape.width / dist[0];
      for (let i = 0; i < dist[0]; i++) {
        chairs.push({ x: seg * (i + 0.5) - half, y: -chairSize, rotation: 0 });
      }
    }
    if (dist[1] > 0) {
      const seg = shape.width / dist[1];
      for (let i = 0; i < dist[1]; i++) {
        chairs.push({ x: seg * (i + 0.5) - half, y: shape.height, rotation: 180 });
      }
    }
    if (dist[2] > 0) {
      const seg = shape.height / dist[2];
      for (let i = 0; i < dist[2]; i++) {
        chairs.push({ x: -chairSize, y: seg * (i + 0.5) - half, rotation: 270 });
      }
    }
    if (dist[3] > 0) {
      const seg = shape.height / dist[3];
      for (let i = 0; i < dist[3]; i++) {
        chairs.push({ x: shape.width, y: seg * (i + 0.5) - half, rotation: 90 });
      }
    }
  } else if (shape.type === 'circle') {
    const radius = shape.width / 2;
    const dist = radius + half;
    for (let i = 0; i < max; i++) {
      const angle = (i * 2 * Math.PI) / max;
      chairs.push({
        x: radius + dist * Math.cos(angle) - half,
        y: radius + dist * Math.sin(angle) - half,
        rotation: angle * 180 / Math.PI + 90
      });
    }
  }

  return chairs;
};

export interface RoomMapProps {
  floor: any;
  occupiedTableIds: Set<string>;
  waitingTableIds: Set<string>;
  reservedTableMeta: Map<string, ReservedTableMeta>;
  tableTooltipsById: Map<string, string>;
  selectedTableId: string | null;
  onSelectTable: (id: string) => void;
}

const ROOM_TABLE_ORDER_STORAGE_KEY = 'reservo-room-table-order';

type RoomTableDropPosition = 'before' | 'after';

interface RoomTableDropIndicator {
  tableId: string;
  groupKey: string;
  position: RoomTableDropPosition;
}

const getStoredRoomTableOrder = (floorId?: string | number | null): string[] => {
  if (!floorId || typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(`${ROOM_TABLE_ORDER_STORAGE_KEY}:${String(floorId)}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((tableId) => String(tableId)).filter(Boolean);
  } catch (error) {
    return [];
  }
};

const persistRoomTableOrder = (floorId?: string | number | null, orderedTableIds: Array<string | number> = []) => {
  if (!floorId || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      `${ROOM_TABLE_ORDER_STORAGE_KEY}:${String(floorId)}`,
      JSON.stringify(
        [...new Set((Array.isArray(orderedTableIds) ? orderedTableIds : []).map((tableId) => String(tableId)).filter(Boolean))]
      )
    );
  } catch (error) {
    // Ignora falhas de armazenamento local.
  }
};

export const getNormalizedRoomTableOrder = (
  tables: any[] = [],
  orderedTableIds: Array<string | number> = [],
) => {
  const floorTableIds = (Array.isArray(tables) ? tables : []).map((table: any) => String(table.id));
  const normalizedOrderedIds = [...new Set(
    (Array.isArray(orderedTableIds) ? orderedTableIds : [])
      .map((tableId) => String(tableId))
      .filter(Boolean)
  )];

  return [
    ...normalizedOrderedIds.filter((tableId) => floorTableIds.includes(tableId)),
    ...floorTableIds.filter((tableId) => !normalizedOrderedIds.includes(tableId)),
  ];
};

export const moveRoomTableToTarget = (
  orderedTableIds: string[] = [],
  sourceTableId: string,
  targetTableId: string,
  position: RoomTableDropPosition = 'before',
) => {
  if (!sourceTableId || !targetTableId || sourceTableId === targetTableId) {
    return orderedTableIds;
  }

  const filteredOrder = orderedTableIds.filter((tableId) => tableId !== sourceTableId);
  const targetIndex = filteredOrder.indexOf(targetTableId);
  if (targetIndex === -1) {
    return orderedTableIds;
  }

  const insertIndex = position === 'after' ? targetIndex + 1 : targetIndex;
  const nextOrder = [...filteredOrder];
  nextOrder.splice(insertIndex, 0, sourceTableId);
  return nextOrder;
};

export const RoomLayoutMap: React.FC<RoomMapProps> = ({ floor, occupiedTableIds, waitingTableIds, reservedTableMeta, tableTooltipsById, selectedTableId, onSelectTable }) => {
  const floorWidth = floor?.width || 800;
  const floorHeight = floor?.height || 600;

  return (
    <div className="room-floor-view-container">
      <div
        className="room-floor-view"
        style={{
          backgroundImage: floor?.texture ? `url(/textures/${floor.texture}.jpg)` : 'none',
          width: `${floorWidth}px`,
          height: `${floorHeight}px`,
          minWidth: `${floorWidth}px`,
          minHeight: `${floorHeight}px`
        }}
      >
        {floor?.tables?.map((table: any) => {
          const isOccupied = occupiedTableIds.has(String(table.id));
          const isWaitingGuest = waitingTableIds.has(String(table.id));
          const isSelected = String(selectedTableId) === String(table.id);
          const reserveInfo = reservedTableMeta.get(String(table.id));
          const isReservedForSelected = !isOccupied && !isWaitingGuest && Boolean(reserveInfo?.hasSelectedReservation);
          const isReserved = !isOccupied && !isWaitingGuest && Number(reserveInfo?.count || 0) > 0;
          const tableStateClass = isOccupied
            ? 'occupied'
            : isWaitingGuest
              ? 'awaiting-guest'
            : isReservedForSelected
              ? 'reserved-priority'
              : isReserved
                ? 'reserved'
                : 'available';
          const tableStateLabel = isOccupied
            ? 'Ocupada'
            : isWaitingGuest
              ? 'Aguardando'
            : isReservedForSelected
              ? 'Reserva selecionada'
              : isReserved
                ? 'Reservada no dia'
                : 'Livre';
          const tableTooltip = tableTooltipsById.get(String(table.id)) || undefined;
          const chairs = getChairPositions(table);
          const tableStyle: React.CSSProperties = {
            left: `${table.position.x - table.shape.width / 2}px`,
            top: `${table.position.y - table.shape.height / 2}px`,
            width: `${table.shape.width}px`,
            height: `${table.shape.height}px`,
            borderRadius: table.shape.type === 'circle' ? '50%' : '6px',
            transform: `rotate(${table.rotation}deg)`,
            ...(table.useColor ? {
              backgroundColor: table.color || '#8B4513'
            } : {
              backgroundImage: `url(/textures/${table.texture}.jpg)`
            })
          };

          return (
            <div
              key={table.id}
              className={`room-table ${tableStateClass} ${isSelected ? 'selected' : ''}`}
              style={tableStyle}
              title={tableTooltip}
              onClick={() => onSelectTable(String(table.id))}
            >
              <div className="room-table-label">
                <span className="room-table-name">{table.name}</span>
                <span className={`room-table-status ${tableStateClass}`}>
                  {tableStateLabel}
                </span>
              </div>
              {chairs.map((chair, index) => (
                <div
                  key={index}
                  className="room-chair"
                  style={{
                    left: chair.x,
                    top: chair.y,
                    transform: `rotate(${chair.rotation}deg)`
                  }}
                />
              ))}
            </div>
          );
        })}
        {floor?.elements?.map((element: any) => {
          const elementStyle: React.CSSProperties = {
            left: `${element.position.x - element.width / 2}px`,
            top: `${element.position.y - element.height / 2}px`,
            width: `${element.width}px`,
            height: `${element.height}px`,
            borderRadius: element.shape === 'circle' ? '50%' : '4px',
            transform: `rotate(${element.rotation}deg)`,
            backgroundColor: element.color || '#e9c46a'
          };
          return (
            <div key={element.id} className="room-floor-element" style={elementStyle}>
              <span style={{ color: element.textColor || '#000000' }}>{element.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const RoomTableMap: React.FC<RoomMapProps> = ({ floor, occupiedTableIds, waitingTableIds, reservedTableMeta, tableTooltipsById, selectedTableId, onSelectTable }) => {
  const [orderedTableIds, setOrderedTableIds] = useState<string[]>(() => getStoredRoomTableOrder(floor?.id));
  const [draggedTableId, setDraggedTableId] = useState<string | null>(null);
  const [draggedGroupKey, setDraggedGroupKey] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<RoomTableDropIndicator | null>(null);

  useEffect(() => {
    const storedOrder = getStoredRoomTableOrder(floor?.id);
    const nextOrderedIds = getNormalizedRoomTableOrder(floor?.tables, storedOrder);

    setOrderedTableIds((currentOrder) => {
      const normalizedCurrentOrder = getNormalizedRoomTableOrder(floor?.tables, currentOrder);
      const hasSameOrder = areStringArraysEqual(normalizedCurrentOrder, nextOrderedIds);

      return hasSameOrder ? normalizedCurrentOrder : nextOrderedIds;
    });
    setDraggedTableId(null);
    setDraggedGroupKey(null);
    setDropIndicator(null);
  }, [floor?.id, floor?.tables]);

  const getDropPosition = useCallback((event: React.DragEvent<HTMLButtonElement>): RoomTableDropPosition => {
    const targetRect = event.currentTarget.getBoundingClientRect();
    const gridRect = event.currentTarget.parentElement?.getBoundingClientRect();
    const isSingleColumn = gridRect
      ? targetRect.width >= gridRect.width - 24
      : window.innerWidth < 640;

    if (isSingleColumn) {
      return event.clientY >= targetRect.top + (targetRect.height / 2) ? 'after' : 'before';
    }

    return event.clientX >= targetRect.left + (targetRect.width / 2) ? 'after' : 'before';
  }, []);

  const groupedTables = useMemo(() => {
    const groups = new Map<string, { min: number; max: number; tables: any[] }>();
    const orderIndexById = new Map(
      orderedTableIds.map((tableId, index) => [String(tableId), index])
    );
    const orderedTables = [...(floor?.tables || [])].sort((firstTable: any, secondTable: any) => {
      const firstOrder = orderIndexById.get(String(firstTable.id));
      const secondOrder = orderIndexById.get(String(secondTable.id));
      const normalizedFirstOrder = firstOrder === undefined ? Number.MAX_SAFE_INTEGER : firstOrder;
      const normalizedSecondOrder = secondOrder === undefined ? Number.MAX_SAFE_INTEGER : secondOrder;

      if (normalizedFirstOrder !== normalizedSecondOrder) {
        return normalizedFirstOrder - normalizedSecondOrder;
      }

      return String(firstTable?.name || '').localeCompare(String(secondTable?.name || ''));
    });

    orderedTables.forEach((table: any) => {
      const min = Math.max(1, Math.floor(Number(table.minCovers) || 1));
      const max = Math.max(min, Math.floor(Number(table.maxCovers) || min));
      const key = `${min}-${max}`;
      if (!groups.has(key)) {
        groups.set(key, { min, max, tables: [] });
      }
      groups.get(key)?.tables.push(table);
    });
    return Array.from(groups.values()).sort((a, b) => {
      if (a.min === b.min) return a.max - b.max;
      return a.min - b.min;
    });
  }, [floor, orderedTableIds]);

  const moveTableToTarget = useCallback((
    sourceTableId: string,
    targetTableId: string,
    position: RoomTableDropPosition = 'before',
  ) => {
    setOrderedTableIds((currentOrder) => {
      const baseOrder = getNormalizedRoomTableOrder(floor?.tables, currentOrder);
      const nextOrder = moveRoomTableToTarget(baseOrder, sourceTableId, targetTableId, position);
      if (areStringArraysEqual(baseOrder, nextOrder)) {
        return currentOrder;
      }
      persistRoomTableOrder(floor?.id, nextOrder);
      return nextOrder;
    });
  }, [floor?.id, floor?.tables]);

  if (!groupedTables.length) {
    return <div className="room-empty">Nenhuma mesa cadastrada para este ambiente.</div>;
  }

  return (
    <div className="room-table-groups">
      {groupedTables.map((group) => (
        <div key={`${group.min}-${group.max}`} className="room-table-group">
          <div className="room-table-group-header">
            <h4>{group.min}-{group.max} pessoas</h4>
            <span className="room-table-group-meta">
              {group.tables.length} mesa{group.tables.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className={`room-table-grid ${draggedGroupKey === `${group.min}-${group.max}` ? 'is-dragging' : ''}`}>
            {group.tables.map((table) => {
              const groupKey = `${group.min}-${group.max}`;
              const tableId = String(table.id);
              const isOccupied = occupiedTableIds.has(String(table.id));
              const isWaitingGuest = waitingTableIds.has(String(table.id));
              const isSelected = String(selectedTableId) === tableId;
              const reserveInfo = reservedTableMeta.get(String(table.id));
              const isReservedForSelected = !isOccupied && !isWaitingGuest && Boolean(reserveInfo?.hasSelectedReservation);
              const isReserved = !isOccupied && !isWaitingGuest && Number(reserveInfo?.count || 0) > 0;
              const isDragged = draggedTableId === tableId;
              const isDropTarget = !isDragged
                && dropIndicator?.groupKey === groupKey
                && dropIndicator?.tableId === tableId;
              const dropTargetClass = isDropTarget ? `drop-target-${dropIndicator?.position}` : '';
              const tableStateClass = isOccupied
                ? 'occupied'
                : isWaitingGuest
                  ? 'awaiting-guest'
                : isReservedForSelected
                  ? 'reserved-priority'
                  : isReserved
                    ? 'reserved'
                    : 'available';
              const tableStateLabel = isOccupied
                ? 'Ocupada'
                : isWaitingGuest
                  ? 'Aguardando'
                : isReservedForSelected
                  ? 'Reserva selecionada'
                  : isReserved
                    ? 'Reservada no dia'
                    : 'Livre';
              const tableTooltip = tableTooltipsById.get(String(table.id)) || undefined;
              return (
                <div
                  key={table.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Mesa ${table.name}`}
                  className={`room-table-card ${tableStateClass} ${isSelected ? 'selected' : ''} ${isDragged ? 'is-dragging' : ''} ${isDropTarget ? 'is-drop-target' : ''} ${dropTargetClass}`}
                  title={tableTooltip}
                  onClick={() => onSelectTable(String(table.id))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectTable(String(table.id));
                    }
                  }}
                  draggable
                  onDragStart={(event) => {
                    setDraggedTableId(tableId);
                    setDraggedGroupKey(groupKey);
                    setDropIndicator(null);
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', tableId);
                  }}
                  onDragOver={(event) => {
                    if (draggedGroupKey && draggedGroupKey !== groupKey) {
                      setDropIndicator(null);
                      return;
                    }
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    if (!draggedTableId) return;

                    if (draggedTableId === tableId) {
                      setDropIndicator(null);
                      return;
                    }

                    const position = getDropPosition(event as unknown as React.DragEvent<HTMLButtonElement>);
                    setDropIndicator((currentIndicator) => {
                      if (
                        currentIndicator
                        && currentIndicator.tableId === tableId
                        && currentIndicator.groupKey === groupKey
                        && currentIndicator.position === position
                      ) {
                        return currentIndicator;
                      }
                      return { tableId, groupKey, position };
                    });
                  }}
                  onDrop={(event) => {
                    if (draggedGroupKey && draggedGroupKey !== groupKey) {
                      setDraggedTableId(null);
                      setDraggedGroupKey(null);
                      setDropIndicator(null);
                      return;
                    }
                    event.preventDefault();
                    const sourceTableId = event.dataTransfer.getData('text/plain') || draggedTableId;
                    const dropPosition = (
                      dropIndicator?.tableId === tableId
                      && dropIndicator?.groupKey === groupKey
                    )
                      ? dropIndicator.position
                      : getDropPosition(event as unknown as React.DragEvent<HTMLButtonElement>);
                    moveTableToTarget(String(sourceTableId || ''), tableId, dropPosition);
                    setDraggedTableId(null);
                    setDraggedGroupKey(null);
                    setDropIndicator(null);
                  }}
                  onDragEnd={() => {
                    setDraggedTableId(null);
                    setDraggedGroupKey(null);
                    setDropIndicator(null);
                  }}
                >
                  <span className={`room-table-card-status ${tableStateClass}`}>
                      {tableStateLabel}
                    </span>
                  <div className="room-table-card-header">
                    <span className="room-table-card-name">{table.name}</span>
                    <span className="room-table-card-drag" aria-hidden="true" title="Arrastar para reordenar">
                      <GripVertical size={16} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

